'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import React, { Suspense, useState } from 'react';
import { encodePassphrase, generateRoomId, randomString } from '@/lib/client-utils';

function Tabs(props: React.PropsWithChildren<{}>) {
  const searchParams = useSearchParams();
  const tabIndex = searchParams?.get('tab') === 'legal' ? 1 : 0;

  const router = useRouter();
  function onTabSelected(index: number) {
    const tab = index === 1 ? 'legal' : 'business';
    router.push(`/?tab=${tab}`);
  }

  let tabs = React.Children.map(props.children, (child, index) => {
    return (
      <button
        className={`
          px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300
          ${
            tabIndex === index
              ? 'bg-[#183C5A] text-white shadow-lg'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }
        `}
        onClick={() => {
          if (onTabSelected) {
            onTabSelected(index);
          }
        }}
        aria-pressed={tabIndex === index}
      >
        {/* @ts-ignore */}
        {child?.props.label}
      </button>
    );
  });

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="flex justify-center mb-8">
        <div className="inline-flex gap-2 bg-gray-100 p-2 rounded-full shadow-md">{tabs}</div>
      </div>
      <div className="transition-all duration-300">
        {/* @ts-ignore */}
        {props.children[tabIndex]}
      </div>
    </div>
  );
}

function BusinessDemoTab(props: { label: string }) {
  const router = useRouter();
  const [e2ee, setE2ee] = useState(false);
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(64));

  const startMeeting = () => {
    if (e2ee) {
      router.push(`/rooms/${generateRoomId()}#${encodePassphrase(sharedPassphrase)}?demo=biz`);
    } else {
      router.push(`/rooms/${generateRoomId()}?demo=biz`);
    }
  };

  return (
    <div className="text-center">
      <div className="bg-white shadow-md rounded-lg p-12 mb-8 min-h-[200px] flex items-center justify-center">
        <p className="text-[#183C5A] text-lg">Check out Blue, attending a Sales meeting .</p>
      </div>
      <button
        onClick={startMeeting}
        className="px-8 py-3 bg-[#183C5A] text-white rounded-lg font-medium hover:bg-gray-700 transition-colors duration-200 shadow-md hover:shadow-lg"
      >
        Start Meeting
      </button>
    </div>
  );
}

function LegalDemoTab(props: { label: string }) {
  const router = useRouter();
  const [e2ee, setE2ee] = useState(false);
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(64));

  const startMeeting = () => {
    if (e2ee) {
      router.push(`/rooms/${generateRoomId()}#${encodePassphrase(sharedPassphrase)}?demo=law`);
    } else {
      router.push(`/rooms/${generateRoomId()}?demo=law`);
    }
  };

  return (
    <div className="text-center">
      <div className="bg-white border-2 border-[#183C5A] rounded-lg p-12 mb-8 min-h-[200px] flex items-center justify-center">
        <p className="text-[#183C5A] text-lg">Check out Blue, attending a Legal meeting .</p>
      </div>
      <button
        onClick={startMeeting}
        className="px-8 py-3 bg-[#183C5A] text-white rounded-lg font-medium hover:bg-gray-700 transition-colors duration-200 shadow-md hover:shadow-lg"
      >
        Start Meeting
      </button>
    </div>
  );
}

export default function Page() {
  return (
    <main className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Grid background pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-40 bg-[]#f0f3f7]" />

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-12">
        <div className="w-full max-w-4xl mx-auto text-center">
          {/* Main title */}
          <h1 className="text-5xl md:text-6xl font-bold text-[#1e3a8a] mb-4 tracking-tight">
            MEET WITH BLUE
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-gray-700 mb-12">
            Have Blue attend a meeting on your
            <br />
            behalf!
          </p>

          {/* Tabs component */}
          <Suspense
            fallback={
              <div className="flex justify-center items-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            }
          >
            <Tabs>
              <BusinessDemoTab label="Business Demo" />
              <LegalDemoTab label="Legal Demo" />
            </Tabs>
          </Suspense>
        </div>
      </div>
    </main>
  );
}
